import React, { useState, useEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { TranslationProvider } from '../../shared/TranslationProvider'
import { useTranslation } from '~/hooks/useTranslation'
import { Typography } from '~/components/_atoms'
import { Stack, Page, Modal } from '~/components/_layouts'
import YnsIcon from '~/components/icons/YnsIcon'
import translations from '../locales.json'
import LiveCoachChat from './LiveCoachChat'
import CoachChatHistory from './CoachChatHistory'
import { useIsMobile } from '~/hooks/useMediaQuery'
import TabButton from '~/components/shared/TabNav/TabButton'
import TabNav from '~/components/shared/TabNav/TabNav'
import { PanelContainer, TabPanel } from '~/components/shared/TabNav/index'
import ButtonContainer from '~/components/shared/TabNav/ButtonContainer'
import { UserCircleIcon, PhoneIcon } from '@heroicons/react/24/outline'

const LiveChat = ({ chat_history = false }) => {
  const { t } = useTranslation()
  const [showHolidaysModal, setShowHolidaysModal] = useState(false)

  const activeTab = chat_history ? 'chat-history' : 'live-chat'

  const isMobile = useIsMobile()

  const holidays = t('holidays', {
    returnObjects: true,
  })

  useEffect(() => {
    const holidaysLink = document.getElementById('chat-holidays-link')
    if (holidaysLink) {
      holidaysLink.addEventListener('click', () => setShowHolidaysModal(true))
      return () => {
        holidaysLink.removeEventListener('click', () =>
          setShowHolidaysModal(true),
        )
      }
    }
  }, [])

  return (
    <>
      <Page
        title={t('title')}
        header={<Typography>{t('description')}</Typography>}
        variant="coach-services"
        afterContent={
          <TabNav defaultActiveTab={activeTab} mobileMode="swipe">
            <TabNavButtons />
            <PanelContainer asChild>
              <TabPanel name="live-chat">
                <LiveCoachChat />
              </TabPanel>
              <TabPanel name="chat-history">
                <CoachChatHistory />
              </TabPanel>
            </PanelContainer>
          </TabNav>
        }
      >
        <Stack spacing={3}>
          <div className="grid grid-cols-[1fr_auto] items-center gap-5">
            <div>
              <div className="flex items-center gap-2">
                <div className="flex h-[50px] w-[50px] items-center justify-center rounded-full bg-intoo-green-medium-700">
                  <YnsIcon name="call" size={24} className="text-white" />
                </div>
                <Typography variant="title4" color="secondary">
                  {isMobile ? t('overview.title_mobile') : t('overview.title')}
                </Typography>
              </div>
              <Typography variant="body1">
                {t('overview.description')}
              </Typography>
            </div>
            <img
              src="https://www-yns-com.s3.amazonaws.com/images/coach_chat/coach-chat-banner.png"
              className="tw-hidden xl:block"
            />
          </div>
        </Stack>
      </Page>
      <Modal
        title="Live Coach Chat Holidays"
        open={showHolidaysModal}
        onClose={() => setShowHolidaysModal(false)}
        size="sm"
        variant="secondary"
      >
        <ul className="list-disc pl-4">
          {holidays.map((holiday, index) => (
            <li key={index} className="mb-2">
              {holiday}
            </li>
          ))}
        </ul>
      </Modal>
    </>
  )
}

export default function LiveChatWithProviders({ locale, ...props }) {
  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope="javascript.components.coach_services.LiveChat"
    >
      <BrowserRouter>
        <LiveChat {...props} />
      </BrowserRouter>
    </TranslationProvider>
  )
}

const Wrapper = ({ children }) => {
  const isMobile = useIsMobile()
  if (isMobile) return children

  return (
    <div className="relative w-full overflow-x-scroll sm:static sm:overflow-auto">
      {children}
    </div>
  )
}

const TabNavButtons = () => {
  const isMobile = useIsMobile()
  const titleClass = isMobile ? '' : 'text-lg mx-0'
  const activeDecorationClass = 'bg-intoo-green-coachcta'

  return (
    <Wrapper>
      <ButtonContainer
        carouselWrapperClass="min-h-[4.5rem] mb-8"
        className="mb-6 flex flex-row justify-between gap-5 min-[530px]:justify-start min-[530px]:gap-10"
        buttonClass="min-w-16"
        titleClass={titleClass}
      >
        <TabButton
          name={'live-chat'}
          text={'Live Coach Chat'}
          Icon={UserCircleIcon}
          activeDecorationClass={activeDecorationClass}
        />
        <TabButton
          name={'chat-history'}
          text={'Coach Chat History'}
          Icon={PhoneIcon}
          activeDecorationClass={activeDecorationClass}
        />
      </ButtonContainer>
    </Wrapper>
  )
}
